import CornerBg from "@/assets/img/illustrations/corner-2.png";
import Background from "@/components/atoms/Background";
import EmailLink from "@/components/atoms/EmailLink";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { useClickToCall } from "@/hooks/useClickToCall";
import { CardDetailsProps } from "@/interfaces/Card";
import Person from "@/interfaces/Person";
import { useGetPersonQuery } from "@/services/people";
import { faEye, faPhone, faPlus } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ElementRef, FC, useEffect, useRef } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import CardHeader from "../../card-headers/CardHeader";
import StorePersonNoteModal from "../../modals/person/StorePersonNoteModal";

const PersonDetailsCard: FC<CardDetailsProps<Person>> = ({
  id,
  card,
  showDetailsButton,
  callBack,
}) => {
  const StorePersonNoteModalRef =
    useRef<ElementRef<typeof StorePersonNoteModal>>(null);

  const { data, isLoading, isError, error } = useGetPersonQuery(id);
  const onCall = useClickToCall();

  useEffect(() => {
    data && callBack?.(data);
  }, [data]);

  const onTriggerModal = () =>
    StorePersonNoteModalRef.current?.showOrHiddenModal();

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div
      className={classNames({
        card: card,
      })}
    >
      {card && <CardHeader title="Informations de l'employé" />}
      <div
        className={classNames("bg-light", {
          "card-body": card,
        })}
      >
        <StorePersonNoteModal ref={StorePersonNoteModalRef} id={id} />
        <Card className="border rounded-3 bg-white dark__bg-1000 shadow-none">
          <Background
            image={CornerBg}
            className="bg-card d-none d-sm-block d-xl-none"
          />
          <Card.Body className="row g-0 flex-column flex-sm-row flex-xl-column z-index-1 align-items-center">
            <Col sm={8} md={6} lg={4} xl={12} className="ps-sm-1 ps-xl-0">
              <Flex alignItems="xl-center" direction="column">
                <p className="fw-semi-bold text-800 mb-0">{data!.fullname}</p>
              </Flex>
              <Flex justifyContent="center" className="gap-2">
                {showDetailsButton ? (
                  <IconButton
                    variant="primary"
                    icon={faEye}
                    iconAlign="left"
                    href={`/people/${data!.id}`}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 mt-2 text-nowrap"
                  >
                    <span className="fs--2">Détails</span>
                  </IconButton>
                ) : (
                  <IconButton
                    variant="primary"
                    icon={faPlus}
                    iconAlign="left"
                    onClick={onTriggerModal}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 mt-2 text-nowrap"
                  >
                    <span className="fs--2">Note</span>
                  </IconButton>
                )}
                {data?.phone && (
                  <IconButton
                    variant="nolina-default"
                    icon={faPhone}
                    iconAlign="left"
                    transform="shrink-3 down-1"
                    size="sm"
                    onClick={() => onCall(data.phone!)}
                    className="w-50 mt-2 "
                  >
                    <span className="fs--2">{data!.phone}</span>
                  </IconButton>
                )}
              </Flex>
            </Col>
          </Card.Body>
        </Card>
        <Row className="border rounded-3 p-x1 mt-3 bg-white dark__bg-1000 mx-0 g-0">
          <Col md={6} xl={12} className="pe-md-4 pe-xl-0">
            <div className="mb-2">
              <Form.Label>#</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.id ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Nom</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.lastname ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Prénom</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.firstname ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Email</Form.Label>
              <br />
              {data?.email ? (
                <EmailLink email={data!.email} />
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Numéro de téléphone</Form.Label>
              <br />
              {data?.phone ? (
                <PhoneCallLink number={data.phone} />
              ) : (
                <UnknownBadge />
              )}
            </div>
          </Col>
          <Col md={6} xl={12} className="ps-md-4 ps-xl-0">
            <div className="mb-2">
              <Form.Label>Numéro de sécurité social</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.socialSecurityNumber ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Titre du poste :</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.jobTitle ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Type de contrat : </Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.contractType ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Adresse :</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.address ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Date de naissance :</Form.Label>
              {data?.birthdate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.birthdate)
                    .setLocale("fr")
                    .toFormat("dd LLLL yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Ville de naissance :</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.cityOfBirth ?? <UnknownBadge />}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Date d'embauche :</Form.Label>
              {data?.employmentStartDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.employmentStartDate)
                    .setLocale("fr")
                    .toFormat("dd LLLL yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PersonDetailsCard;
